import { useState, useEffect, memo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Av, Bdg, Toggle, PH } from '../../components/shared';
import { useApp } from '../../context/AppContext';
import * as api from '../../services/api';

const ProfileSettings = ({ profileId, profile, cardSt }) => {
  const { showToast } = useApp();
  const [direct, setDirect] = useState(!!profile.user_direct);
  const [priv, setPriv] = useState(!!profile.private);
  const [leadsOn, setLeadsOn] = useState(!!profile.is_leads_enabled);

  const handleToggleDirect = async () => {
    try {
      const res = await api.toggleDirect(profileId);
      setDirect(!!res.user_direct);
      showToast(res.message || "Direct mode updated");
    } catch (e) { showToast(e.message || "Failed to update", "error"); }
  };

  const handleTogglePrivate = async () => {
    try {
      const res = await api.togglePrivate(profileId);
      setPriv(!!res.private);
      showToast(res.message || "Privacy updated");
    } catch (e) { showToast(e.message || "Failed to update", "error"); }
  };

  const handleToggleLeads = async () => {
    try {
      await api.updateMember(profileId, { is_leads_enabled: !leadsOn });
      setLeadsOn(!leadsOn);
      showToast(leadsOn ? "Lead capture disabled" : "Lead capture enabled");
    } catch (e) { showToast(e.message || "Failed to update", "error"); }
  };

  return (
    <div style={{ ...cardSt, padding: "26px", flex: 1 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 16 }}>Profile Settings</div>
      {[
        { lbl: "Direct Mode", val: direct, desc: "Skip profile, go to top link", onToggle: handleToggleDirect },
        { lbl: "Private Profile", val: priv, desc: "Hide from public search", onToggle: handleTogglePrivate },
        { lbl: "Leads Enabled", val: leadsOn, desc: "Capture lead forms", onToggle: handleToggleLeads },
      ].map(({ lbl, val, desc, onToggle }) => (
        <div key={lbl} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid rgba(0,0,0,.05)" }}>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a" }}>{lbl}</div>
            <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 2 }}>{desc}</div>
          </div>
          <Toggle on={val} onChange={onToggle} />
        </div>
      ))}
    </div>
  );
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const ActivityTab = ({ taps, views, leads, connects, cardSt }) => {
  const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const weekData = dayNames.map(d => ({ d, t: 0, v: 0, l: 0 }));
  const dist = [.07,.12,.18,.14,.22,.19,.08];
  dist.forEach((r, i) => { weekData[i].t = Math.round(taps * r); weekData[i].v = Math.round(views * r); });
  leads.forEach(l => {
    if (!l.created_at) return;
    const day = new Date(l.created_at).getDay();
    weekData[day].l++;
  });
  const week = [...weekData.slice(1), weekData[0]];
  const maxBar = Math.max(...week.map(d => Math.max(d.t, d.v, d.l)), 1);

  const feed = [
    ...leads.slice(0, 15).map(l => ({
      ico: "🎯", txt: `Lead captured — ${l.name || 'Unknown'}`, sub: l.company || l.email || "—",
      time: l.created_at, c: "#dc2626",
    })),
    ...connects.slice(0, 15).map(c => ({
      ico: "🤝", txt: `New connection — ${c.name || 'Unknown'}`, sub: `@${c.username || '—'}`,
      time: c.created_at, c: "#059669",
    })),
  ].sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0)).slice(0, 8);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 22 }}>
      <div style={{ ...cardSt, padding: "28px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", letterSpacing: "-.2px" }}>Weekly Activity</div>
            <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 3 }}>Last 7 days — Taps, Views, Leads</div>
          </div>
          <div style={{ display: "flex", gap: 14 }}>
            {[["#0891b2", "Taps"], ["#7c3aed", "Views"], ["#059669", "Leads"]].map(([c, l]) => (
              <div key={l} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: c }} />
                <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 550 }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, height: 180, alignItems: "flex-end", padding: "0 4px" }}>
          {week.map((d, i) => (
            <div key={d.d} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, height: "100%", justifyContent: "flex-end" }}>
              <div style={{ width: "100%", display: "flex", gap: 2, alignItems: "flex-end", height: "calc(100% - 20px)" }}>
                {[{ v: d.t, c: "#0891b2" }, { v: d.v, c: "#7c3aed" }, { v: d.l, c: "#059669" }].map(({ v, c }, j) => (
                  <div key={j} style={{
                    flex: 1,
                    height: `${Math.max((v / maxBar) * 100, 2)}%`,
                    background: `linear-gradient(180deg,${c},${c}66)`,
                    borderRadius: "4px 4px 0 0",
                    boxShadow: `0 -2px 8px ${c}33`,
                    transition: `height 1.1s cubic-bezier(.16,1,.3,1) ${(i * .06) + (j * .02)}s`,
                  }} />
                ))}
              </div>
              <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 600 }}>{d.d}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 16, paddingTop: 14, borderTop: "1px solid rgba(0,0,0,.06)" }}>
          {week.map(d => (
            <div key={d.d} style={{ flex: 1, textAlign: "center", padding: "8px 4px", borderRadius: 9, background: "rgba(0,0,0,.03)" }}>
              <div style={{ fontSize: 12.5, fontWeight: 760, color: "#0f172a" }}>{d.t + d.v + d.l}</div>
              <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>{d.d}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...cardSt, padding: "28px" }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 18 }}>Recent Activity</div>
        {feed.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--t4)", fontSize: 12 }}>No recent activity</div>
        ) : feed.map((ev, i) => (
          <div key={i} style={{ display: "flex", gap: 11, padding: "10px 0", borderBottom: i < feed.length - 1 ? "1px solid rgba(0,0,0,.05)" : "none" }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: `${ev.c}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>{ev.ico}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.txt}</div>
              <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 2 }}>{ev.sub}</div>
            </div>
            <div style={{ fontSize: 10, color: "#94a3b8", flexShrink: 0, whiteSpace: "nowrap" }}>{timeAgo(ev.time)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PageUserAnalytics = memo(() => {
  const { showToast } = useApp();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const profileId = searchParams.get('id');

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!profileId) { navigate('/team/users'); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await api.getMemberManage(profileId);
        setData(res.data || res);
      } catch (e) {
        showToast(e.message || "Failed to load profile data", "error");
        navigate('/team/users');
      } finally { setLoading(false); }
    })();
  }, [profileId]);

  if (loading) return <div className="pg fi"><div style={{padding:60,textAlign:"center",color:"var(--t3)",fontSize:14}}>Loading analytics...</div></div>;
  if (!data) return null;

  const profile = data.profile || {};
  const taps = profile.taps || 0;
  const views = profile.tiks || 0;
  const totalLeads = data.total_leads || 0;
  const totalConnects = data.total_connects || 0;
  const totalClicks = data.total_clicks || 0;
  const totalPlatforms = data.total_platforms || 0;
  const cardStatus = data.card_status || "—";
  const leads = data.leads || [];
  const connects = data.connects || [];
  const platforms = data.platforms || [];

  const ctr = views > 0 ? Math.round((taps / views) * 100) : 0;
  const conv = totalConnects > 0 ? Math.round((totalLeads / totalConnects) * 100) : 0;
  const engagement = views > 0 ? Math.round((totalConnects / views) * 100) : 0;

  const KPIS = [
    { k: "Total Taps", v: taps.toLocaleString(), ico: "📲", c: "#0891b2" },
    { k: "Profile Views", v: views.toLocaleString(), ico: "👁", c: "#7c3aed" },
    { k: "Connections", v: totalConnects.toLocaleString(), ico: "🤝", c: "#059669" },
    { k: "Total Clicks", v: totalClicks.toLocaleString(), ico: "🔖", c: "#d97706" },
    { k: "Leads", v: totalLeads.toLocaleString(), ico: "🎯", c: "#dc2626" },
    { k: "Engagement", v: engagement + "%", ico: "✦", c: "#6366f1" },
  ];

  const TABS = [
    { id: "overview", ico: "⬡", lbl: "Overview" },
    { id: "activity", ico: "📈", lbl: "Activity" },
    { id: "sources", ico: "🔀", lbl: "Sources" },
  ];

  const cardSt = {
    background: "rgba(255,255,255,.72)",
    border: "1px solid rgba(255,255,255,.9)",
    borderRadius: 18,
    boxShadow: "0 4px 20px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)",
  };

  return (
    <div className="pg fi">
      {/* ── TOP NAV ── */}
      <div style={{
        display: "flex", alignItems: "center",
        background: "rgba(255,255,255,.72)",
        backdropFilter: "blur(40px)",
        border: "1px solid rgba(255,255,255,.85)",
        borderRadius: 16,
        boxShadow: "0 2px 16px rgba(0,0,0,.06)",
        marginBottom: 24,
      }}>
        <button onClick={() => navigate('/team/users')} style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "14px 20px", border: "none",
          background: "transparent", cursor: "pointer",
          color: "#64748b", fontSize: 13, fontFamily: "var(--f)",
          borderRight: "1px solid rgba(0,0,0,.06)",
          transition: "all .15s", flexShrink: 0,
        }}
          onMouseEnter={e => e.currentTarget.style.color = "#0f172a"}
          onMouseLeave={e => e.currentTarget.style.color = "#64748b"}>
          <span style={{ fontSize: 16 }}>←</span> Members
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 13, padding: "10px 22px", flex: 1 }}>
          {profile.photo ? (
            <img src={profile.photo} alt={profile.name} style={{ width: 38, height: 38, borderRadius: 11, objectFit: "cover", flexShrink: 0 }} />
          ) : (
            <Av name={profile.name ? profile.name.slice(0, 2).toUpperCase() : "??"} grad="linear-gradient(150deg,#64748b,#059669)" size={38} />
          )}
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", letterSpacing: "-.2px" }}>{profile.name}</div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{profile.job_title || profile.company || "—"}</div>
          </div>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: profile.active ? "#22c55e" : "#94a3b8", boxShadow: profile.active ? "0 0 6px #22c55e" : "none" }} />
        </div>

        <div style={{ display: "flex", alignSelf: "stretch" }}>
          {TABS.map(({ id, ico, lbl }) => (
            <button key={id} onClick={() => setTab(id)} style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "0 22px", border: "none",
              background: tab === id ? "rgba(255,255,255,.95)" : "transparent",
              cursor: "pointer", fontSize: 12.5, fontWeight: tab === id ? 700 : 500,
              fontFamily: "var(--f)",
              color: tab === id ? "#0f172a" : "#94a3b8",
              borderBottom: tab === id ? "2.5px solid #0891b2" : "2.5px solid transparent",
              transition: "all .18s",
            }}
              onMouseEnter={e => { if (tab !== id) e.currentTarget.style.color = "#475569"; }}
              onMouseLeave={e => { if (tab !== id) e.currentTarget.style.color = "#94a3b8"; }}>
              <span style={{ fontSize: 14 }}>{ico}</span>{lbl}
            </button>
          ))}
        </div>

        <div style={{
          padding: "5px 15px", borderRadius: 20, marginRight: 22, flexShrink: 0,
          background: "rgba(8,145,178,.08)", border: "1px solid rgba(8,145,178,.18)",
          color: "#0891b2", fontSize: 11, fontWeight: 700,
        }}>Card: {cardStatus}</div>
      </div>

      {/* ══ OVERVIEW ══ */}
      {tab === "overview" && (
        <div style={{ maxWidth: "100%" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 14, marginBottom: 28 }}>
            {KPIS.map(({ k, v, ico, c }) => (
              <div key={k} style={{ ...cardSt, padding: "20px 16px", position: "relative", overflow: "hidden", cursor: "default", transition: "all .2s" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 28px rgba(0,0,0,.1),0 0 0 2px ${c}22`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)"; }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${c},${c}44)`, borderRadius: "18px 18px 0 0" }} />
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${c}14`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, marginBottom: 14 }}>{ico}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: "#0f172a", letterSpacing: "-1.5px", lineHeight: 1 }}>{v}</div>
                <div style={{ fontSize: 11, color: c, marginTop: 7, fontWeight: 660 }}>{k}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 22 }}>
            {/* Funnel */}
            <div style={{ ...cardSt, padding: "26px 28px" }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 22 }}>Conversion Funnel</div>
              {[
                { lbl: "Profile Views", val: views, pct: 100, c: "#0891b2" },
                { lbl: "Taps", val: taps, pct: views > 0 ? Math.round(taps / views * 100) : 0, c: "#7c3aed" },
                { lbl: "Connections", val: totalConnects, pct: views > 0 ? Math.round(totalConnects / views * 100) : 0, c: "#059669" },
                { lbl: "Leads Captured", val: totalLeads, pct: views > 0 ? Math.round(totalLeads / views * 100) : 0, c: "#dc2626" },
              ].map(({ lbl, val, pct, c }) => (
                <div key={lbl} style={{ marginBottom: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7, alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 7, height: 7, borderRadius: "50%", background: c }} />
                      <span style={{ fontSize: 12.5, color: "#475569", fontWeight: 560 }}>{lbl}</span>
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>{val.toLocaleString()}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: c, minWidth: 36, textAlign: "right" }}>{pct}%</span>
                    </div>
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: "rgba(0,0,0,.06)", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg,${c},${c}88)`, borderRadius: 4, transition: "width 1.2s cubic-bezier(.16,1,.3,1)" }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Metrics 2x2 */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, alignContent: "start" }}>
              {[
                { lbl: "Tap-Through Rate", val: ctr + "%", c: "#0891b2", sub: "Taps ÷ Views", ico: "📲" },
                { lbl: "Conversion Rate", val: conv + "%", c: "#059669", sub: "Leads ÷ Connections", ico: "🔄" },
                { lbl: "Total Platforms", val: totalPlatforms, c: "#d97706", sub: "Active links", ico: "📅" },
                { lbl: "Engagement", val: engagement + "%", c: "#7c3aed", sub: "Connects ÷ Views", ico: "🔖" },
              ].map(({ lbl, val, c, sub, ico }) => (
                <div key={lbl} style={{ ...cardSt, padding: "20px 18px" }}>
                  <div style={{ fontSize: 20, marginBottom: 10 }}>{ico}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: c, letterSpacing: "-1.5px", lineHeight: 1 }}>{val}</div>
                  <div style={{ fontSize: 11.5, color: "#475569", marginTop: 8, fontWeight: 600 }}>{lbl}</div>
                  <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>{sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══ ACTIVITY ══ */}
      {tab === "activity" && <ActivityTab taps={taps} views={views} leads={leads} connects={connects} cardSt={cardSt} />}

      {/* ══ SOURCES ══ */}
      {tab === "sources" && (
        <div style={{ maxWidth: "100%", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 }}>
          {/* Platform Links */}
          <div style={{ ...cardSt, padding: "28px" }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 24 }}>
              Platform Links ({totalPlatforms})
            </div>
            {platforms.length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: "var(--t4)", fontSize: 12 }}>No platform links added</div>
            ) : platforms.map((p, i) => {
              const maxClicks = Math.max(...platforms.map(x => x.clicks || 0), 1);
              return (
                <div key={p.id || i} style={{ marginBottom: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {p.icon && <img src={p.icon} alt="" style={{ width: 18, height: 18, borderRadius: 4 }} />}
                      <span style={{ fontSize: 13, fontWeight: 650, color: "#334155" }}>{p.title || p.label || "Link"}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>{(p.clicks || 0).toLocaleString()} clicks</span>
                      <Bdg s={p.active ? "active" : "paused"} />
                    </div>
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: "rgba(0,0,0,.06)", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${Math.max((p.clicks || 0) / maxClicks * 100, 2)}%`, background: "linear-gradient(90deg,#0891b2,#0891b288)", borderRadius: 4, transition: `width 1.2s cubic-bezier(.16,1,.3,1) ${i * .1}s` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Stats summary */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ ...cardSt, padding: "26px" }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 16 }}>Profile Summary</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 11 }}>
                {[
                  { lbl: "Card Status", val: cardStatus, c: cardStatus === "active" ? "#059669" : "#d97706" },
                  { lbl: "Total Platforms", val: totalPlatforms, c: "#0891b2" },
                  { lbl: "Tap-Through", val: ctr + "%", c: "#7c3aed" },
                  { lbl: "Lead Conv.", val: conv + "%", c: "#dc2626" },
                ].map(({ lbl, val, c }) => (
                  <div key={lbl} style={{ padding: "14px", borderRadius: 12, background: `${c}08`, border: `1px solid ${c}18` }}>
                    <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: c }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Direct & Private toggles */}
            <ProfileSettings profileId={profileId} profile={profile} cardSt={cardSt} />
          </div>
        </div>
      )}
    </div>
  );
});
